import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Field, Form, Formik, getIn, useFormikContext } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { Attachment, AttachmentPage } from 'app/edozvola/cases/cases.model';
import * as Yup from 'yup';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { Button, Col, FormGroup, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { Input, Label } from 'edozvola-frontend-lib/dist/ui';
import { ERRORS, messages } from 'app/config/constants';
import { deleteAttachment, fetchAttachments } from 'app/edozvola/cases/service/cases.service';
import { showToast } from 'app/shared/util/toast.util';
import { toast } from 'react-toastify';
import GenericTableViewForm from 'app/shared/generic-table-view-form/generic-table-view-form';
import { Column } from 'app/shared/generic-table-view-form/generic-table-view-form.model';
import { PaginationState } from 'app/shared/pagination/pagination.model';
import { queryClient } from 'app/app';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faTrash } from '@fortawesome/free-solid-svg-icons';
import {updateCaseRequestAttachmentsMutation} from "app/edozvola/cases/service/cases.queries";

const AttachmentPageForm = () => {
  const { id } = useParams<{ id: string }>();
  const [initialData, setInitialData] = useState<AttachmentPage>(null);
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [idToDelete, setIdToDelete] = useState<number>(null);
  const {mutate: saveMutation, isPending: isSaving} = updateCaseRequestAttachmentsMutation(id, data => {
    setInitialData(data);
    showToast(toast.success, messages.DATA_SAVED);
  }, error => {
    if (Object.values(ERRORS).includes(error.data.message)) {
      showToast(toast.error, error.data.message);
    } else {
      showToast(toast.error, ERRORS.GENERIC_ERROR);
    }
  });

  const validation = Yup.object({
    shortDescription: Yup.string().required(messages.MANDATORY_FIELD),
    proofOfLegalInterest: Yup.string().required(messages.MANDATORY_FIELD),
    locationPermit: Yup.string().required(messages.MANDATORY_FIELD),
  });

  const [pagination, setPagination] = useState<PaginationState>({
    total: 0,
    page: 1,
    size: 10,
    column: 'idCaseAttachment',
    direction: 'asc',
  });

  const columns: Column<Attachment>[] = useMemo(
    () => [
      { header: 'Naziv privitka', field: 'name' },
      { header: 'Vrsta priloga', field: 'type' },
      { header: 'Datum unosa', field: 'entryDate' },
    ],
    [],
  );

  const renderRow = useCallback(
    (attachment: Attachment, index: number) => (
      <tr key={index}>
        <td>{attachment.name} </td>
        <td>{attachment.type.value} </td>
        <td>{attachment.entryDate ? attachment.entryDate.toDateString() : ''}</td>
        <td className="text-center">
          <Button color={'info'} onClick={() => navigate('/cases/' + id + '/request-attachments/' + attachment.idCaseAttachment)}>
            <FontAwesomeIcon icon={faMagnifyingGlass} className="font-size-large" />
          </Button>
          {initialData?.attachments?.length > 1 && (
            <Button color={'danger'} onClick={() => onDeleteAttachmentClicked(attachment.idCaseAttachment)}>
              <FontAwesomeIcon icon={faTrash} className="font-size-large" />
            </Button>
          )}
        </td>
      </tr>
    ),
    [],
  );

  useEffect(() => {
    setLoading(true);

    fetchAttachments(id).then(value => {
      setInitialData(value);
    });

    setLoading(false);
  }, []);

  const handleSubmit = (values, { setSubmitting }) => {
    saveMutation(values);
  };

  const onDeleteAttachmentClicked = useCallback((idAttachment: number) => {
    setIdToDelete(idAttachment);
    setShowDeleteModal(true);
  }, []);

  const onConfirmDeleteAttachmentClicked = () => {
    deleteAttachment(id, idToDelete)
      .then(attachments => {
        setShowDeleteModal(false);
        setIdToDelete(null);

        setInitialData({ ...initialData, attachments: attachments });
        queryClient.invalidateQueries({ queryKey: ['generic-table', { title: 'Prilozi' }] });

        showToast(toast.success, messages.DATA_SAVED);
      })
      .catch(error => {
        if (Object.values(ERRORS).includes(error.data.message)) {
          showToast(toast.error, error.data.message);
        } else {
          showToast(toast.error, ERRORS.GENERIC_ERROR);
        }
      });
  };

  const FormikValueObserver = ({ onChange }) => {
    const { dirty } = useFormikContext();

    useEffect(() => onChange({ dirty }), [dirty, onChange]);

    return null;
  };

  return (
    <>
      <Formik
        enableReinitialize={true}
        validateOnMount={true}
        initialValues={initialData}
        isInitialValid={false}
        validationSchema={validation}
        onSubmit={handleSubmit}
      >
        {({ errors, isValid, dirty, submitForm, setFieldValue }) => (
          <Form>
            <FormikValueObserver
              onChange={dirty => {
                if (dirty.dirty) {
                  dispatch(setFormChanged(true));
                } else {
                  dispatch(initCaseFormChange());
                }
              }}
            />

            <Row>
              <Col xs={12}>
                <FormGroup>
                  <Label labelText={'Kratki opis zahtjeva'} />
                  <Field
                    id="shortDescription"
                    name="shortDescription"
                    render={({ field }) => <Input {...field} type="textarea" error={getIn(errors, 'shortDescription')} />}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className={'pt-5 pb-5'}>
              <Col xs={12}>
                <GenericTableViewForm<Attachment>
                  title="Prilozi"
                  data={initialData?.attachments}
                  renderRow={renderRow}
                  columns={columns}
                  pagination={pagination}
                  setPagination={setPagination}
                  addNewButtonName={'Dodaj novi privitak'}
                  addNewLink={'/cases/' + id + '/request-attachments/' + 0}
                  modalProps={{
                    showModal: showDeleteModal,
                    onConfirmDelete: onConfirmDeleteAttachmentClicked,
                    onCancel: () => setShowDeleteModal(false),
                  }}
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12}>
                <FormGroup>
                  <Label labelText={'Dokaz pravnog interesa'} />
                  <Field
                    id="proofOfLegalInterest"
                    name="proofOfLegalInterest"
                    render={({ field }) => <Input {...field} type="textarea" error={getIn(errors, 'proofOfLegalInterest')} />}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <FormGroup>
                  <Label labelText={'Lokacijska dozvola'} />
                  <Field
                    id="locationPermit"
                    name="locationPermit"
                    render={({ field }) => <Input {...field} type="textarea" error={getIn(errors, 'locationPermit')} />}
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="case-save-button">
              <Button color={'info'} size={'lg'} isLoading={isSaving} onClick={submitForm} disabled={!dirty || !isValid}>
                Spremi
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AttachmentPageForm;
